/**
 * CampaignInputManager - Gates player input to the active campaign's supported
 * input methods (HostCampaignConfig.supportedInputs) in the coordinator path.
 *
 * GitHub #13807: a campaign should be able to declare which input methods it
 * supports (e.g. touch-only games should not grant gamepad). This manager is the
 * coordinator-native enforcement point — it clamps each player peer's
 * `PeerPermissions` so `InputManager` drops disallowed inbound input.
 *
 * Sources of truth and triggers:
 * - PavilionCoordinator owns the campaign config (`getCampaignConfig()`); its
 *   `campaignConfigLoaded` (fires only with authoritative fetched config) and
 *   `campaignCleared` events drive a recompute for all connected players.
 * - PeerCoordinator's `onPlayerJoined` event clamps a newly joined player, then
 *   reconciles. Admin edits to an assigned campaign are never pushed to the host,
 *   so a joining player would otherwise be clamped against a stale allowlist and
 *   lose an input the dashboard reports as enabled (#13862). The clamp is applied
 *   from the cached config first and a refresh only produces a second
 *   `setInputAccess` if it leaves the peer's live permissions out of date (the
 *   two triggers above can write to that peer mid-refresh). Clamping must not wait on
 *   that fetch: until it runs the peer holds `DEFAULT_PERMISSIONS.player` (every
 *   input on), which `InputManager` would honor for disallowed input.
 *
 * Permissions are always recomputed from `DEFAULT_PERMISSIONS.player` (the base
 * the coordinator path assigns on connect), so tightening and loosening both
 * resolve correctly and a cleared campaign restores defaults.
 *
 * After clamping, the player client is notified of its granted access via
 * `setInputAccess` (through `PeerInputAccessNotifier`) so it can bind/unbind the
 * matching capture listeners (notably touch). Without this, a client that froze
 * a default-off touch snapshot would never start capturing touch even though the
 * host is willing to forward it.
 */

import type { PeerCoordinator } from '../PeerCoordinator';
import type { PavilionCoordinator } from '../../pavilion/PavilionCoordinator';
import { DEFAULT_PERMISSIONS } from '../types';
import type { PeerPermissions } from '../types';
import { clampPeerPermissionsToCampaign } from '../campaignPermissions';
import { PeerInputAccessNotifier } from '../peerInputAccess';

export type CampaignInputManagerDeps = {
  peerCoordinator: PeerCoordinator;
  pavilionCoordinator: PavilionCoordinator;
  getHostAuthId: () => string;
};

export class CampaignInputManager {
  private readonly disposers: Array<() => void> = [];
  private readonly notifier: PeerInputAccessNotifier;
  private isDisposed = false;

  constructor(private readonly deps: CampaignInputManagerDeps) {
    this.notifier = new PeerInputAccessNotifier({
      peerCoordinator: deps.peerCoordinator,
      getHostAuthId: deps.getHostAuthId,
    });
  }

  public init(): void {
    const { peerCoordinator, pavilionCoordinator } = this.deps;

    this.disposers.push(
      pavilionCoordinator.events.campaignConfigLoaded.addListener(() => this.applyToAllPlayers()),
      pavilionCoordinator.events.campaignCleared.addListener(() => this.applyToAllPlayers()),
      peerCoordinator.events.onPlayerJoined.addListener(({ peerId }) => {
        void this.applyToPeer(peerId);
      }),
    );
  }

  public dispose(): void {
    this.isDisposed = true;
    for (const dispose of this.disposers) {
      dispose();
    }
    this.disposers.length = 0;
  }

  private supportedInputs() {
    // Only honor config that belongs to the currently assigned campaign.
    // `PavilionCoordinator` leaves the previous campaign's `config` in the store
    // after a clear (which only resets `campaignId`/`reason`) and during the
    // pre-fetch window of a switch. In both cases the stale config must not be
    // applied, otherwise a cleared/switched campaign would keep the old
    // restrictions instead of restoring default player permissions.
    const { campaignId, config } = this.deps.pavilionCoordinator.getCampaignDetails();
    if (!campaignId || config?.campaignId !== campaignId) {
      return undefined;
    }
    return config.supportedInputs;
  }

  private applyToAllPlayers(): void {
    const permissions = this.resolvePermissions();
    this.deps.peerCoordinator.updateAllPlayerPermissions(permissions);
    this.notifier.notifyAllPlayers(permissions);
  }

  private async applyToPeer(peerId: string): Promise<void> {
    // Clamp synchronously. A joining peer holds `DEFAULT_PERMISSIONS.player` —
    // every input on, including touch — and `InputManager` forwards inbound input
    // against those permissions, so any await before this point would let a
    // restricted campaign accept disallowed input (#13807 / #13843).
    this.sendPermissions(peerId, this.resolvePermissions());

    // The cached config may predate an admin's input-method change, which is
    // never pushed to the host, so re-clamp once a refresh confirms the current
    // allowlist (#13862). Nothing blocks on this: if the fetch hangs, the player
    // keeps the grant already sent above.
    await this.deps.pavilionCoordinator.refreshCampaignConfig();
    if (this.isDisposed) return;

    // Reconcile against what the peer actually holds, not the grant sent above: a
    // `campaignConfigLoaded` / `campaignCleared` during the await runs
    // `applyToAllPlayers()` over this peer too, and can leave it looser than the
    // refreshed allowlist. Absent permissions mean it disconnected mid-refresh.
    const current = this.currentPlayerPermissions(peerId);
    if (!current) return;

    const refreshed = this.resolvePermissions();
    if (!this.permissionsDiffer(current, refreshed)) return;

    this.sendPermissions(peerId, refreshed);
  }

  private sendPermissions(peerId: string, permissions: PeerPermissions): void {
    this.deps.peerCoordinator.updatePeerPermissions(peerId, permissions);
    this.notifier.notifyPeer(peerId, permissions);
  }

  private currentPlayerPermissions(peerId: string): PeerPermissions | undefined {
    const peer = this.deps.peerCoordinator.getConnectedPeer(peerId);
    return peer?.peerType === 'player' ? peer.permissions : undefined;
  }

  private permissionsDiffer(a: PeerPermissions, b: PeerPermissions): boolean {
    return a.mouse !== b.mouse || a.keyboard !== b.keyboard || a.touch !== b.touch || a.gamepad !== b.gamepad;
  }

  private resolvePermissions(): PeerPermissions {
    return clampPeerPermissionsToCampaign(DEFAULT_PERMISSIONS.player, this.supportedInputs());
  }
}
